import { motion } from "framer-motion";
import { AlertTriangle, Globe, MoreVertical } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import type { Account } from "@/api/accounts";
import { HealthPill } from "./HealthPill";

interface AccountCardProps {
  account: Account;
  onMenu?: (account: Account) => void;
  onClick?: (account: Account) => void;
}

export function AccountCard({ account, onMenu, onClick }: AccountCardProps) {
  const initials = useMemo(() => extractInitials(account.name), [account.name]);
  const gradient = useMemo(() => avatarGradient(account.id), [account.id]);
  const lastSeenRel = useMemo(() => relTime(account.last_seen_at), [account.last_seen_at]);

  const detail = useMemo(() => {
    if (account.health === "banned") return account.ban_reason ?? null;
    if (account.health === "limited" && account.last_flood_seconds > 0) {
      return formatFloodSeconds(account.last_flood_seconds);
    }
    if (account.health === "paused") return null;
    return lastSeenRel;
  }, [account, lastSeenRel]);

  return (
    <motion.button
      layout
      whileTap={{ scale: 0.985 }}
      onClick={() => onClick?.(account)}
      className={cn(
        "group w-full rounded-2xl p-4 text-left",
        "bg-white/[0.025] border border-white/[0.06] hover:border-white/[0.12]",
        "transition-colors",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className="relative shrink-0 size-12 rounded-2xl grid place-items-center font-semibold text-base text-white overflow-hidden"
          style={{ background: gradient }}
        >
          {account.avatar_url ? (
            <img
              src={account.avatar_url}
              alt=""
              className="absolute inset-0 size-full object-cover"
              loading="lazy"
            />
          ) : (
            initials
          )}
          <span
            className={cn(
              "absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full border-2 border-bg",
              account.health === "online" && "bg-success",
              account.health === "limited" && "bg-warning",
              account.health === "paused" && "bg-text-muted",
              account.health === "banned" && "bg-danger",
              account.health === "idle" && "bg-text-dim",
            )}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[15px] truncate">{account.name}</span>
          </div>
          <div className="text-xs text-text-muted tabular-nums mt-0.5">{account.phone}</div>

          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <HealthPill health={account.health} detail={detail} />
            {account.proxy && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md",
                  account.proxy.is_alive
                    ? "bg-white/[0.04] text-text-muted"
                    : "bg-danger/10 text-danger",
                )}
              >
                {account.proxy.is_alive ? <Globe className="size-3" /> : <AlertTriangle className="size-3" />}
                {account.proxy.country_code && account.proxy.country_code !== "custom"
                  ? account.proxy.country_code.toUpperCase()
                  : account.proxy.proxy_type.toUpperCase()}
              </span>
            )}
          </div>
        </div>

        <div
          onClick={(e) => {
            e.stopPropagation();
            haptic.select();
            onMenu?.(account);
          }}
          className={cn(
            "shrink-0 size-9 rounded-full grid place-items-center",
            "text-text-dim hover:text-white hover:bg-white/[0.06] transition-colors",
            "cursor-pointer",
          )}
          role="button"
          tabIndex={0}
          aria-label="Действия"
        >
          <MoreVertical className="size-4" />
        </div>
      </div>
    </motion.button>
  );
}

function extractInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? "").join("").toUpperCase() || "?";
}

function avatarGradient(id: number): string {
  // Stable gradient per account id — warm/orange palette variants
  const palette = [
    "linear-gradient(135deg, #FF6B35 0%, #FF3D00 100%)",
    "linear-gradient(135deg, #FF8A3D 0%, #FF4500 100%)",
    "linear-gradient(135deg, #FFB347 0%, #FF6B35 100%)",
    "linear-gradient(135deg, #FF4500 0%, #C92A2A 100%)",
    "linear-gradient(135deg, #FF6B35 0%, #8B3A00 100%)",
  ];
  return palette[Math.abs(id) % palette.length];
}

function relTime(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "только что";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} мин`;
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)} ч`;
  if (diffSec < 86_400 * 7) return `${Math.floor(diffSec / 86_400)} дн`;
  return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
}

function formatFloodSeconds(s: number): string {
  if (s < 60) return `${s}с`;
  if (s < 3600) return `${Math.ceil(s / 60)}м`;
  return `${Math.ceil(s / 3600)}ч`;
}
